import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GaTrackingEventAction, GaTrackingEventName, GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { select, Store } from '@ngrx/store';
import { CatalogState } from '~app/modules/catalog/store';
import { getCategory, getCategoryParents } from '~app/modules/generic/store';
import { Observable, Subject } from 'rxjs';
import { Category } from '~app/modules/generic/models';
import { CategoriesService } from '~app/modules/generic/services/categories.service';
import { filter, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'app-related-categories',
  templateUrl: './related-categories.component.html',
  styleUrls: ['./related-categories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelatedCategoriesComponent implements OnInit, OnDestroy {
  categories$: Observable<Category[]>;

  @Input() title: string;
  @Input() isMobile: boolean;

  private destroySubject$ = new Subject<boolean>();

  constructor(
    private gtmDatalayerService: GtmDatalayerService,
    private store$: Store<CatalogState>,
    private categoriesService: CategoriesService
  ) {}

  ngOnInit(): void {
    this.initializeSelectedCategory();
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  initializeRelatedCategoriesView(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_RELATED_CATEGORIES_VIEWED,
      GA_eventname: GaTrackingEventName.Interaction,
      action: GaTrackingEventAction.RELATED_CATEGORIES_VIEWED,
    });
  }

  private initializeSelectedCategory(): void {
    this.categories$ = this.store$.pipe(
      select(getCategory),
      withLatestFrom(this.store$.pipe(select(getCategoryParents))),
      filter(([category, parents]) => !!parents.length),
      switchMap(([category, parents]) => {
        const parentCategory = parents[0];

        return this.categoriesService.getCategoryByIdOrSlug(parentCategory.id).pipe(
          map(({ children }) => children.filter(({ id }) => id !== category.id)) // Filter out unwanted category
        );
      }),
      takeUntil(this.destroySubject$)
    );
  }
}
